/**
 * FluentCart DOM Scraper
 *
 * Reads charity card DOM attributes to augment modal context.
 * The orchestrator delegates all document.querySelector calls here.
 */

import type { FluentCartModalContext } from './fluentcart-modal-context';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function readNullableNumber(value: string | null): number | null {
  if (value === null || value.trim() === '') {
    return null;
  }

  const parsed = Number.parseInt(value, 10);
  return Number.isNaN(parsed) ? null : parsed;
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

export function getCharityCardContext(context: Partial<FluentCartModalContext>): Partial<FluentCartModalContext> {
  if (context.charityId === null || context.charityId === undefined) {
    return context;
  }

  const card = document.querySelector<HTMLElement>(`[data-charity-post-id="${context.charityId}"]`);
  if (!card) {
    throw new Error(`FluentCart could not find a charity card for charity ID ${context.charityId}.`);
  }

  const donateAnchor = card.querySelector<HTMLAnchorElement>('a.pifster-donate-btn');
  if (!donateAnchor) {
    throw new Error(`FluentCart could not find a donate anchor for charity ID ${context.charityId}.`);
  }

  const charityName = donateAnchor.getAttribute('data-charity-name')
    || card.querySelector<HTMLElement>('.monthly-charity-title')?.textContent?.trim()
    || '';
  const periodId = context.periodId ?? readNullableNumber(donateAnchor.getAttribute('data-period-id'))
    ?? readNullableNumber(card.getAttribute('data-period-id'));
  const ratioAttr = donateAnchor.getAttribute('data-donation-vote-ratio')
    || card.getAttribute('data-donation-vote-ratio')
    || '';
  const capAttr = donateAnchor.getAttribute('data-bonus-vote-cap')
    || card.getAttribute('data-bonus-vote-cap')
    || '';

  const bonusRatio = ratioAttr !== '' && !Number.isNaN(Number.parseFloat(ratioAttr))
    ? Number.parseFloat(ratioAttr)
    : (context.bonusRatio ?? null);
  const bonusCap = capAttr !== '' && !Number.isNaN(Number.parseInt(capAttr, 10))
    ? Number.parseInt(capAttr, 10)
    : (context.bonusCap ?? null);
  const donateHrefFallback = donateAnchor.getAttribute('href') ?? '';
  const modeAttr = donateAnchor.getAttribute('data-mode');
  const mode: FluentCartModalContext['mode'] = modeAttr === 'donate_only' ? 'donate_only' : (context.mode ?? 'vote_plus_donate');

  return {
    ...context,
    periodId,
    charityName,
    donateHrefFallback,
    bonusRatio,
    bonusCap,
    mode,
  };
}
